export const STATUS_LABEL = {
  todo: "To Do",
  inprogress: "In Progress",
  deployment: "Deployment",
  qa: "QA",
  inreview: "In Review",
  completed: "Completed",
};

// Weight applied to a task's estimated hours to approximate "hours logged so far",
// used only because real time-tracking (progress_history) isn't populated yet.
const STATUS_WEIGHT = {
  todo: 0,
  inprogress: 0.5,
  deployment: 0.9,
  qa: 0.75,
  inreview: 0.85,
  completed: 1,
  unknown: 0,
};

const normalizeStatus = (status) => {
  if (!status) return "unknown";
  return String(status).trim().toLowerCase().replace(/[\s_-]+/g, "");
};

export const PIE_COLORS = ["#2563eb", "#f59e0b", "#0891b2", "#7c3aed", "#16a34a", "#d12465", "#dc2626"];

export const parseHours = (hourString) => {
  if (!hourString) return 0;
  let total = 0;
  String(hourString)
    .split(",")
    .forEach((p) => {
      p = p.trim().toLowerCase();
      if (!p) return;
      if (p.includes("d")) total += parseFloat(p) * 8;
      else if (p.includes("h")) total += parseFloat(p);
      else if (p.includes("min")) total += parseFloat(p) / 60;
      else if (p.includes("sec")) total += parseFloat(p) / 3600;
      else total += parseFloat(p) || 0;
    });
  return total;
};

export const computeTaskStats = (tasks = []) => {
  const total = tasks.length;
  const normalized = tasks.map((t) => ({ ...t, _norm: normalizeStatus(t.status) }));

  const completedCount = normalized.filter((t) => t._norm === "completed").length;
  const inProgressCount = normalized.filter((t) => t._norm === "inprogress").length;
  const todoCount = normalized.filter((t) => t._norm === "todo").length;
  const reviewCount = normalized.filter((t) => t._norm === "inreview").length;
  const completionRate = total ? Math.round((completedCount / total) * 100) : 0;

  const statusMap = {};
  normalized.forEach((t) => {
    const label = STATUS_LABEL[t._norm] || t.status || "Unknown";
    statusMap[label] = (statusMap[label] || 0) + 1;
  });
  const byStatus = Object.entries(statusMap).map(([name, value]) => ({ name, value }));

  const priorityMap = {};
  tasks.forEach((t) => {
    const raw = (t.priority || "Medium").trim();
    const label = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    priorityMap[label] = (priorityMap[label] || 0) + 1;
  });
  const byPriority = Object.entries(priorityMap).map(([name, value]) => ({ name, value }));

  const highPriorityCount = tasks.filter((t) =>
    ["high", "urgent"].includes((t.priority || "").trim().toLowerCase())
  ).length;

  let estimatedHours = 0;
  let loggedHours = 0;
  let overdueCount = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  normalized.forEach((t) => {
    const taskHours = parseHours(t.assigned_hours || t.time);
    estimatedHours += taskHours;

    const weight = STATUS_WEIGHT[t._norm] ?? 0;
    loggedHours += taskHours * weight;

    if (t._norm !== "completed" && t.end_date) {
      const due = new Date(t.end_date);
      if (!isNaN(due.getTime()) && due < today) overdueCount += 1;
    }
  });

  estimatedHours = Math.round(estimatedHours * 10) / 10;
  loggedHours = Math.round(loggedHours * 10) / 10;

  const efficiency = estimatedHours > 0
    ? Math.min(100, Math.round((loggedHours / estimatedHours) * 100))
    : 0;

  return {
    total,
    completedCount,
    inProgressCount,
    todoCount,
    reviewCount,
    completionRate,
    byStatus,
    byPriority,
    highPriorityCount,
    overdueCount,
    estimatedHours,
    loggedHours,
    efficiency,
  };
};